import React, { useEffect, useMemo, useState, memo } from 'react';
import { CalendarIcon, ClockIcon, MapPinIcon, MonitorIcon, MoreVerticalIcon, FilterIcon, ChevronDownIcon, LoaderIcon } from 'lucide-react';
import { collection, collectionGroup, query, where, getDocs, getDoc, doc, orderBy, Timestamp, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { StudentLessonDetailModal } from '../modals/StudentLessonDetailModal';
import { StudentCancelLessonModal } from '../modals/StudentCancelLessonModal';
import { ensureStudentLink } from '../../services/studentService';
import toast from 'react-hot-toast';
import { getLessonDisplayStatus } from '../../utils/lessonStatusHelpers';
type Lesson = {
  id: string;
  teacherId: string;
  title: string;
  teacherName: string;
  instrument: string;
  startAt: Date;
  durationMinutes: number;
  location: string;
  status: string;
  notes: string;
  format: string;
  meetingLink?: string;
  // Required to distinguish No-Show from Completed at render time.
  // Mark No-Show writes `status: 'COMPLETED'` + `attended: false`, so the
  // status field alone is not enough — both fields must be passed to the
  // canonical `getLessonDisplayStatus` helper.
  attended?: boolean;
  cancelled?: boolean;
};
type FilterOption = 'All' | 'Upcoming' | 'Past';
// Maximum number of past lessons rendered on the main Student Schedule
// page. Beyond this count, the section shows a 'View All Past Lessons →'
// CTA instead of rendering the full historical archive. Older lessons
// remain accessible via the future dedicated history route (not built yet
// — see TODO at the CTA below).
const PAST_LESSONS_LIMIT = 5;
export const StudentSchedule = () => {
  const {
    currentUser
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isLoading, setIsLoading] = useState(true);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [filter, setFilter] = useState<FilterOption>('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [lessonToCancel, setLessonToCancel] = useState<Lesson | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  useEffect(() => {
    if (currentUser) {
      fetchLessons();
    }
  }, [currentUser]);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  const fetchLessons = async () => {
    try {
      setIsLoading(true);
      if (!currentUser) return;
      // Step 0: Self-healing self-link.
      // Before querying for roster docs, attempt to (re)link any teacher
      // roster docs that match this student's email to the current Firebase
      // auth uid. This heals missing OR stale linkedUid values so the
      // collectionGroup('students') query below returns the correct results.
      // ensureStudentLink is internally deduped (30s in-memory cache) so this
      // is safe to call on every fetch.
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        const studentEmail = userDocSnap.exists() && (userDocSnap.data() as any)?.email ? (userDocSnap.data() as any).email as string : currentUser.email || '';
        if (studentEmail) {
          await ensureStudentLink(studentEmail, currentUser.uid);
        } else {
          console.warn('[MySchedule] No email available for ensureStudentLink; skipping self-heal');
        }
      } catch (e) {
        console.error('[MySchedule] ensureStudentLink failed:', e);
        // Non-fatal — continue with the existing query. If linkedUid is
        // already correct, lessons will still load.
      }
      // Step 1: Find teacher-student links (same as dashboard)
      const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', currentUser.uid));
      const studentsSnap = await getDocs(studentsQuery);
      if (studentsSnap.empty) {
        console.warn('[MySchedule] studentsQuery returned EMPTY for uid:', currentUser.uid, '— roster docs may be missing linkedUid OR no teacher has added ' + 'this student yet. See [ensureStudentLink] logs above for ' + 'diagnostic details.');
        setLessons([]);
        setIsLoading(false);
        return;
      }
      // Build student-teacher pairs
      const studentTeacherPairs: {
        studentId: string;
        teacherId: string;
      }[] = [];
      studentsSnap.docs.forEach(docSnap => {
        const pathSegments = docSnap.ref.path.split('/');
        const teacherId = pathSegments[1];
        const studentData = docSnap.data();
        const studentIdForLessons = studentData.studentId || docSnap.id;
        studentTeacherPairs.push({
          studentId: studentIdForLessons,
          teacherId
        });
      });
      console.log('[MySchedule] Found student-teacher pairs:', studentTeacherPairs.length);
      // Step 2: Query ALL lessons (past + future) — PHASE C DUAL-READ.
      //
      // Two query shapes run in parallel:
      //
      //   (1) PRIMARY — collectionGroup('calendar')
      //          where('linkedUid', '==', currentUser.uid)
      //       One query covers ALL teachers because linkedUid is the
      //       student's auth uid (denormalized at lesson write time +
      //       healed by the teacher-side per-mount backfill). This is the
      //       shape the Phase D Firestore rule will eventually require.
      //
      //   (2) FALLBACK — collectionGroup('calendar')
      //          where('studentId', '==', pair.studentId)
      //       Per-pair, matching the pre-Phase-C behaviour. Kept until
      //       Phase 5 cleanup so lessons that pre-date the linkedUid
      //       backfill (or were created while a student was unlinked)
      //       still surface during the migration window.
      //
      // Both branches use orderBy('startAt','asc') — required to match the
      // existing composite indexes (studentId + startAt asc) and the new
      // Phase B index (linkedUid + startAt asc). We sort + filter further
      // in JS after merging. NO startAt range filter here — this page
      // intentionally loads past + future lessons.
      //
      // Each branch is wrapped in try/catch returning an empty docs[]
      // shape on failure. CRITICAL: after the Phase D1 rule tightens
      // the `/{path=**}/calendar/{lessonId}` collection-group rule, the
      // legacy `studentId` query will start returning permission-denied
      // — without these guards, the schedule render would crash.
      //
      // De-duplication is keyed on the lesson doc's FULL Firestore path
      // (`teachers/{teacherId}/calendar/{lessonId}`) rather than the
      // doc id alone, because lesson ids are only unique within a
      // teacher's subcollection.
      const primaryPromise = getDocs(query(collectionGroup(db, 'calendar'), where('linkedUid', '==', currentUser.uid), orderBy('startAt', 'asc'))).catch(err => {
        console.warn('[MySchedule] dual-read primary (linkedUid) query failed; ' + 'falling through to legacy results only', err);
        return {
          docs: [] as any[]
        };
      });
      const fallbackPromises = studentTeacherPairs.map(pair => getDocs(query(collectionGroup(db, 'calendar'), where('studentId', '==', pair.studentId), orderBy('startAt', 'asc'))).catch(err => {
        // After Phase D1, this branch is EXPECTED to throw
        // permission-denied for lessons without a matching linkedUid.
        // We swallow + return [] so the schedule keeps rendering on
        // the primary results alone.
        console.warn('[MySchedule] dual-read legacy (studentId) query failed ' + 'for pair; falling through to []', {
          teacherId: pair.teacherId,
          studentId: pair.studentId,
          err
        });
        return {
          docs: [] as any[]
        };
      }));
      const [primarySnap, ...fallbackSnaps] = await Promise.all([primaryPromise, ...fallbackPromises]);
      // Merge + de-dupe by full Firestore path. The path's second
      // segment is the teacherId, so we can derive it without a
      // surrounding loop variable for the primary branch.
      const lessonsByPath = new Map<string, any>();
      const consumeSnap = (snap: {
        docs: any[];
      }, fallbackTeacherId?: string) => {
        for (const lessonDoc of snap.docs) {
          const fullPath: string = lessonDoc.ref.path;
          if (lessonsByPath.has(fullPath)) continue;
          const data = lessonDoc.data();
          // Same CANCELED/cancelled filter that the legacy code applied
          // — kept here so it runs uniformly across both branches.
          if (data.status === 'CANCELED' || data.cancelled === true) continue;
          // teacherId is the second path segment:
          // teachers / {teacherId} / calendar / {lessonId}
          const pathSegments = fullPath.split('/');
          const teacherId = fallbackTeacherId || pathSegments[1];
          lessonsByPath.set(fullPath, {
            id: lessonDoc.id,
            teacherId,
            ...data
          });
        }
      };
      // Primary branch first — its results win the dedupe race
      // (linkedUid is the canonical claim).
      consumeSnap(primarySnap);
      fallbackSnaps.forEach((snap, i) => {
        consumeSnap(snap, studentTeacherPairs[i]?.teacherId);
      });
      // Phase C observability — counts the migration is converging.
      // `legacyOnlyCount` should trend to 0 as backfill completes;
      // `linkedUidOnlyCount > 0` is fine and expected.
      const primaryPathSet = new Set(primarySnap.docs.map((d: any) => d.ref.path));
      const legacyPathSet = new Set<string>();
      fallbackSnaps.forEach(snap => {
        snap.docs.forEach((d: any) => legacyPathSet.add(d.ref.path));
      });
      let legacyOnlyCount = 0;
      let linkedUidOnlyCount = 0;
      const legacyOnlyLessonIds: string[] = [];
      legacyPathSet.forEach(p => {
        if (!primaryPathSet.has(p)) {
          legacyOnlyCount++;
          if (legacyOnlyLessonIds.length < 10) {
            legacyOnlyLessonIds.push(p);
          }
        }
      });
      primaryPathSet.forEach(p => {
        if (!legacyPathSet.has(p)) linkedUidOnlyCount++;
      });
      console.log('[MySchedule] dual-read diff', {
        uid: currentUser.uid,
        linkedUidResultCount: primarySnap.docs.length,
        legacyResultCount: legacyPathSet.size,
        unionCount: lessonsByPath.size,
        legacyOnlyCount,
        linkedUidOnlyCount,
        legacyOnlyLessonIds
      });
      const allRawLessons = Array.from(lessonsByPath.values());
      console.log('[MySchedule] Total raw lessons after dual-read merge:', allRawLessons.length);
      // Step 3: Resolve teacher names
      const teacherIds = [...new Set(allRawLessons.map(l => l.teacherId))];
      const teacherNameMap: Record<string, string> = {};
      await Promise.all(teacherIds.map(async tid => {
        try {
          const profileDoc = await getDoc(doc(db, 'publicTeacherProfiles', tid));
          if (profileDoc.exists()) {
            const p = profileDoc.data();
            teacherNameMap[tid] = p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Teacher';
          } else {
            teacherNameMap[tid] = 'Teacher';
          }
        } catch {
          teacherNameMap[tid] = 'Teacher';
        }
      }));
      // Step 4: Format lessons
      const formattedLessons: Lesson[] = allRawLessons.map(raw => {
        const startDate = raw.startAt?.toDate ? raw.startAt.toDate() : new Date(raw.startAt);
        const locationStr = raw.location || '';
        const isOnline = locationStr.toLowerCase().includes('online') || locationStr.toLowerCase().includes('virtual') || locationStr.toLowerCase().includes('zoom') || locationStr.toLowerCase().includes('meet') || !!raw.meetingLink;
        return {
          id: raw.id,
          teacherId: raw.teacherId,
          title: raw.title || `${raw.instrument || 'Music'} Lesson`,
          teacherName: teacherNameMap[raw.teacherId] || 'Teacher',
          instrument: raw.instrument || 'Music',
          startAt: startDate,
          durationMinutes: raw.durationMinutes || 60,
          location: locationStr || (isOnline ? 'Online' : 'In-person'),
          status: (raw.status || 'SCHEDULED').toUpperCase(),
          notes: raw.notes || '',
          format: isOnline ? 'Online' : 'In-person',
          meetingLink: raw.meetingLink,
          // Preserve attendance + cancellation fields so the status badge
          // can correctly distinguish No-Show (COMPLETED + attended:false)
          // from Completed (COMPLETED + attended:true/undefined).
          attended: typeof raw.attended === 'boolean' ? raw.attended : undefined,
          cancelled: raw.cancelled === true
        };
      });
      console.log('[MySchedule] Formatted lessons:', formattedLessons.length);
      setLessons(formattedLessons);
    } catch (error) {
      console.error('[MySchedule] Error fetching lessons:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancelLesson = async () => {
    if (!lessonToCancel) return;
    try {
      const lessonRef = doc(db, 'teachers', lessonToCancel.teacherId, 'calendar', lessonToCancel.id);
      await updateDoc(lessonRef, {
        status: 'CANCELED',
        canceledBy: 'student',
        canceledAt: Timestamp.now()
      });
      // Create in-app alert for the teacher (non-blocking)
      try {
        const alertsRef = collection(db, 'teachers', lessonToCancel.teacherId, 'alerts');
        const studentName = currentUser?.displayName || 'A student';
        await addDoc(alertsRef, {
          type: 'lesson_canceled',
          message: `${studentName} canceled a lesson`,
          studentName,
          lessonTime: lessonToCancel.startAt,
          lessonId: lessonToCancel.id,
          reason: 'Lesson canceled by student',
          status: 'active',
          read: false,
          createdAt: serverTimestamp()
        });
      } catch (alertError) {
        console.warn('[MySchedule] Failed to create teacher alert (non-blocking):', alertError);
      }
      // Remove from local state
      setLessons(prev => prev.filter(l => l.id !== lessonToCancel.id));
      setLessonToCancel(null);
      toast.success('Lesson canceled successfully');
    } catch (error) {
      console.error('Error canceling lesson:', error);
      toast.error('Failed to cancel lesson. Please try again.');
    }
  };
  const {
    todayLessons,
    upcomingLessons,
    pastLessons
  } = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    const todayList: Lesson[] = [];
    const upcomingList: Lesson[] = [];
    const pastList: Lesson[] = [];
    lessons.forEach(lesson => {
      const d = lesson.startAt;
      if (lesson.status === 'COMPLETED' || d < todayStart) {
        pastList.push(lesson);
      } else if (d >= todayStart && d < tomorrowStart) {
        todayList.push(lesson);
      } else {
        upcomingList.push(lesson);
      }
    });
    todayList.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    upcomingList.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    pastList.sort((a, b) => b.startAt.getTime() - a.startAt.getTime());
    console.log('[MySchedule] Today:', todayList.length, 'Upcoming:', upcomingList.length, 'Past:', pastList.length);
    return {
      todayLessons: todayList,
      upcomingLessons: upcomingList,
      pastLessons: pastList
    };
  }, [lessons]);
  const todayDateStr = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const formatDateLabel = (d: Date) => {
    return {
      monthDay: d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    };
  };
  const formatTime = (d: Date) => {
    const timeStr = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const match = timeStr.match(/(\d+:\d+)\s*(AM|PM)/i);
    if (!match) return {
      time: timeStr,
      period: ''
    };
    return {
      time: match[1],
      period: match[2].toUpperCase()
    };
  };
  // Source of truth for the student-side status badge.
  //
  // Uses the canonical `getLessonDisplayStatus` helper from
  // utils/lessonStatusHelpers so the student view stays in lock-step with
  // the teacher dashboard. Critically: a past lesson is NOT auto-labeled
  // "Completed" — only the teacher's explicit Mark Completed / No-Show /
  // Cancelled action (which writes `lesson.status` in Firestore) does that.
  //
  // Past lessons whose status is still SCHEDULED / CONFIRMED / missing
  // resolve to the canonical `needs-confirmation` state, which we render
  // here as "Pending Confirmation" (yellow) on the student side. The teacher
  // sees the same lesson as "Needs Confirmation" — same color family,
  // wording adjusted for audience.
  const getStatusTag = (lesson: Lesson) => {
    // Local Lesson type lacks `endAt`; derive it from startAt + duration so
    // the canonical helper has the field it needs.
    const endAt = new Date(lesson.startAt.getTime() + lesson.durationMinutes * 60 * 1000);
    const displayStatus = getLessonDisplayStatus({
      startAt: lesson.startAt,
      endAt,
      status: lesson.status,
      // CRITICAL: `attended` and `cancelled` must be forwarded.
      // - Mark No-Show writes `status: 'COMPLETED'` + `attended: false`
      // - Without `attended`, the helper falls back to 'completed' and
      //   the student sees the wrong badge.
      attended: lesson.attended,
      cancelled: lesson.cancelled
    } as any);
    switch (displayStatus) {
      case 'completed':
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-green-900/30 text-green-400 border border-green-700' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-7912">
            Completed
          </span>;
      case 'no-show':
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-red-900/30 text-red-400 border border-red-700' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-7913">
            No-Show
          </span>;
      case 'cancelled':
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-red-900/30 text-red-400 border border-red-700' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-7914">
            Cancelled
          </span>;
      case 'needs-confirmation':
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-700' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`} data-id="element-7915">
            Pending Confirmation
          </span>;
      case 'in-progress':
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/30 text-blue-400 border border-blue-700' : 'bg-blue-50 text-blue-700 border border-blue-200'}`} data-id="element-7916">
            In Progress
          </span>;
      case 'today':
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-green-900/30 text-green-400 border border-green-700' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-7917">
            Today
          </span>;
      case 'scheduled':
      default:
        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/30 text-blue-400 border border-blue-700' : 'bg-blue-50 text-blue-700 border border-blue-200'}`} data-id="element-7918">
            Scheduled
          </span>;
    }
  };
  const isOnlineFn = (lesson: Lesson) => lesson.format?.toLowerCase() === 'online';
  const LessonCard = ({
    lesson,
    isToday = false,
    isPast = false
  }: {
    lesson: Lesson;
    isToday?: boolean;
    isPast?: boolean;
  }) => {
    const {
      monthDay
    } = formatDateLabel(lesson.startAt);
    const {
      time,
      period
    } = formatTime(lesson.startAt);
    const online = isOnlineFn(lesson);
    // Compute canonical displayStatus once so the card itself (not just
    // the badge) can de-emphasise cancelled / no-show lessons.
    //
    // Visual rules:
    //   - cancelled  → opacity 50%, no hover lift (the lesson is "closed")
    //   - no-show    → opacity 75%, no hover lift (still informational)
    //   - everything else → unchanged appearance + hover lift
    //
    // The badge inside getStatusTag stays at full opacity and full color
    // so the status remains immediately readable even on dimmed cards.
    const endAtForStatus = new Date(lesson.startAt.getTime() + lesson.durationMinutes * 60 * 1000);
    const displayStatus = getLessonDisplayStatus({
      startAt: lesson.startAt,
      endAt: endAtForStatus,
      status: lesson.status,
      attended: lesson.attended,
      cancelled: lesson.cancelled
    } as any);
    const isCancelled = displayStatus === 'cancelled';
    const isNoShow = displayStatus === 'no-show';
    const baseClasses = isDark ? 'bg-navy-800 border border-navy-800' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50';
    const hoverClasses = isCancelled || isNoShow ? '' // No hover lift on de-emphasised cards.
    : isDark ? 'hover:-translate-y-0.5 hover:bg-[#1e2a4a] hover:shadow-lg hover:shadow-blue-500/10' : 'hover:-translate-y-0.5 hover:shadow-md hover:shadow-gray-200/60';
    const dimClasses = isCancelled ? 'opacity-50' : isNoShow ? 'opacity-75' : '';
    return <div onClick={() => setSelectedLesson(lesson)} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl cursor-pointer transition-all duration-200 ease-out ${baseClasses} ${hoverClasses} ${dimClasses}`} data-id="element-7919">
        {/* Date/Time Block */}
        <div className={`flex-shrink-0 w-20 h-20 rounded-xl flex flex-col items-center justify-center ${isToday ? isDark ? 'bg-gradient-to-br from-blue-900/40 to-blue-800/30' : 'bg-gradient-to-br from-blue-50 to-blue-100/80' : isPast ? isDark ? 'bg-navy-900/40' : 'bg-gray-50' : isDark ? 'bg-gradient-to-br from-navy-900/60 to-blue-900/20' : 'bg-gradient-to-br from-blue-50/80 to-slate-100/60'}`} data-id="element-7920">
          {isToday ? <>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7921">
                {time}
              </span>
              <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7922">
                {period}
              </span>
            </> : <>
              <span className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7923">
                {monthDay}
              </span>
              <span className={`text-xs font-medium mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7924">
                {time} {period}
              </span>
            </>}
        </div>

        {/* Center Content */}
        <div className="flex-1 min-w-0" data-id="element-7925">
          <h4 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7926">
            {lesson.title}
          </h4>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7927">
            with {lesson.teacherName}
          </p>
          <div className={`flex items-center gap-3 mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7928">
            <span className="flex items-center gap-1" data-id="element-7929">
              {online ? <MonitorIcon className="h-3.5 w-3.5" data-id="element-7930" /> : <MapPinIcon className="h-3.5 w-3.5" data-id="element-7931" />}
              {online ? 'Online' : 'In-person'}
            </span>
            <span className={isDark ? 'text-gray-600' : 'text-gray-300'} data-id="element-7932">
              ·
            </span>
            <span className="flex items-center gap-1" data-id="element-7933">
              <ClockIcon className="h-3.5 w-3.5" data-id="element-7934" />
              {lesson.durationMinutes} min
            </span>
          </div>
        </div>

        {/* Right: Status + Actions */}
        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-2 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-start" data-id="element-7935">
          <div className="flex items-center gap-2" data-id="element-7936">
            {getStatusTag(lesson)}
            {!isPast && lesson.status !== 'COMPLETED' && lesson.status !== 'CANCELED' && <div className="relative" data-id="element-7937">
                  <button onClick={e => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === lesson.id ? null : lesson.id);
            }} className={`p-1 rounded-md transition-colors ${isDark ? 'hover:bg-navy-700 text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`} data-id="element-7938">
                    <MoreVerticalIcon className="h-4 w-4" data-id="element-7939" />
                  </button>
                  {openMenuId === lesson.id && <div className={`absolute right-0 mt-1 w-36 rounded-lg border shadow-lg z-20 py-1 ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-7940">
                      <button onClick={() => {
                setOpenMenuId(null);
                setLessonToCancel(lesson);
              }} className={`w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors`} data-id="element-7941">
                        Cancel Lesson
                      </button>
                    </div>}
                </div>}
          </div>
          <button onClick={e => {
          e.stopPropagation();
          setSelectedLesson(lesson);
        }} className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${isDark ? 'border-navy-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7942">
            {isPast ? 'View Notes' : 'View Details'}
          </button>
        </div>
      </div>;
  };
  const SectionHeader = ({
    title,
    count,
    badge
  }: {
    title: string;
    count?: number;
    badge?: string;
  }) => <div className="flex items-center gap-3 mb-4" data-id="element-7943">
      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7944">
        {title}
      </h3>
      {badge && <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`} data-id="element-7945">
          {badge}
        </span>}
      {count !== undefined && count > 0 && !badge && <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`} data-id="element-7946">
          {count}
        </span>}
    </div>;
  const EmptySection = ({
    message
  }: {
    message: string;
  }) => <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7947">
      <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-30" data-id="element-7948" />
      <p className="text-sm" data-id="element-7949">{message}</p>
    </div>;
  const showToday = filter === 'All' || filter === 'Upcoming';
  const showUpcoming = filter === 'All' || filter === 'Upcoming';
  const showPast = filter === 'All' || filter === 'Past';
  return <div className="max-w-4xl" data-id="element-7950">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8" data-id="element-7951">
        <div data-id="element-7952">
          <h1 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7953">
            My Schedule
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7954">
            All your music lessons in one place
          </p>
        </div>

        {/* Filter Dropdown */}
        <div className="relative" data-id="element-7955">
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${isDark ? 'bg-navy-800 border-navy-600 text-gray-300 hover:bg-navy-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7956">
            <FilterIcon className="h-4 w-4" data-id="element-7957" />
            Filter
            <ChevronDownIcon className="h-4 w-4" data-id="element-7958" />
          </button>
          {isFilterOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsFilterOpen(false)} data-id="element-7959" />
              <div className={`absolute right-0 mt-2 w-40 rounded-lg border shadow-lg z-20 py-1 ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-200'}`} data-id="element-7960">
                {(['All', 'Upcoming', 'Past'] as FilterOption[]).map(option => <button key={option} onClick={() => {
              setFilter(option);
              setIsFilterOpen(false);
            }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${filter === option ? isDark ? 'bg-navy-700 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-7961">
                      {option}
                    </button>)}
              </div>
            </>}
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading && <div className="space-y-10" data-id="element-7962">
          {/* Today section skeleton */}
          <section data-id="element-7963">
            <div className="flex items-center gap-3 mb-4 animate-pulse" data-id="element-7964">
              <div className={`h-6 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7965" />
              <div className={`h-5 w-20 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7966" />
            </div>
            <div className="space-y-3" data-id="element-7967">
              <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl ${isDark ? 'bg-navy-800 border border-navy-800' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50'}`} data-id="element-7968">
                <div className="animate-pulse flex items-center gap-6 w-full" data-id="element-7969">
                  <div className={`flex-shrink-0 w-20 h-20 rounded-xl ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7970" />
                  <div className="flex-1 min-w-0 space-y-2.5" data-id="element-7971">
                    <div className={`h-4 w-36 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7972" />
                    <div className={`h-3 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7973" />
                    <div className="flex items-center gap-3" data-id="element-7974">
                      <div className={`h-3 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7975" />
                      <div className={`h-3 w-14 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7976" />
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-2" data-id="element-7977">
                    <div className={`h-6 w-20 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7978" />
                    <div className={`h-8 w-24 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7979" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Upcoming section skeleton */}
          <section data-id="element-7980">
            <div className="flex items-center gap-3 mb-4 animate-pulse" data-id="element-7981">
              <div className={`h-6 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7982" />
              <div className={`h-5 w-8 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7983" />
            </div>
            <div className="space-y-3" data-id="element-7984">
              {[1, 2, 3].map(i => <div key={i} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl ${isDark ? 'bg-navy-800 border border-navy-800' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50'}`} data-id="element-7985">
                  <div className="animate-pulse flex items-center gap-6 w-full" data-id="element-7986">
                    <div className={`flex-shrink-0 w-20 h-20 rounded-xl ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7987" />
                    <div className="flex-1 min-w-0 space-y-2.5" data-id="element-7988">
                      <div className={`h-4 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} style={{
                  width: `${100 + i * 20}px`
                }} data-id="element-7989" />
                      <div className={`h-3 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7990" />
                      <div className="flex items-center gap-3" data-id="element-7991">
                        <div className={`h-3 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7992" />
                        <div className={`h-3 w-14 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7993" />
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-2" data-id="element-7994">
                      <div className={`h-6 w-20 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7995" />
                      <div className={`h-8 w-24 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7996" />
                    </div>
                  </div>
                </div>)}
            </div>
          </section>
        </div>}

      {!isLoading && <div className="space-y-10" data-id="element-7997">
          {/* Today Section */}
          {showToday && <section data-id="element-7998">
              <SectionHeader title="Today" badge={todayDateStr} data-id="element-7999" />
              {todayLessons.length > 0 ? <div className="space-y-3" data-id="element-8000">
                  {todayLessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson} isToday data-id="element-8001" />)}
                </div> :
        // Compact empty-Today strip.
        //
        // Replaces the previous full <EmptySection/> hero (which
        // dominated the page on the common "nothing today" case).
        // The strip is ~48–64px tall, single row on desktop, and
        // surfaces the most useful next-step info: when the
        // student's next lesson is. When there are no upcoming
        // lessons either, the secondary line is omitted so we
        // don't render misleading whitespace.
        (() => {
          const next = upcomingLessons[0];
          let nextLabel: string | null = null;
          if (next) {
            const nextStart = next.startAt;
            const todayStart = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
            const tomorrowStart = new Date(todayStart);
            tomorrowStart.setDate(tomorrowStart.getDate() + 1);
            const dayAfterTomorrowStart = new Date(tomorrowStart);
            dayAfterTomorrowStart.setDate(dayAfterTomorrowStart.getDate() + 1);
            const isTomorrow = nextStart >= tomorrowStart && nextStart < dayAfterTomorrowStart;
            const dayPart = isTomorrow ? 'Tomorrow' : nextStart.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            });
            const {
              time,
              period
            } = formatTime(nextStart);
            nextLabel = `Next lesson: ${dayPart} at ${time} ${period}`;
          }
          return <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isDark ? 'bg-navy-900/40 border-navy-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-8002">
                      <CalendarIcon className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8003" />
                      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 min-w-0" data-id="element-8004">
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8005">
                          No lessons today
                        </span>
                        {nextLabel && <>
                            <span className={`hidden sm:inline ${isDark ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-8006">
                              ·
                            </span>
                            <span className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8007">
                              {nextLabel}
                            </span>
                          </>}
                      </div>
                    </div>;
        })()}
            </section>}

          {/* Upcoming Section */}
          {showUpcoming && <section data-id="element-8008">
              <SectionHeader title="Upcoming" count={upcomingLessons.length} data-id="element-8009" />
              {upcomingLessons.length > 0 ? <div className="space-y-3" data-id="element-8010">
                  {upcomingLessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson} data-id="element-8011" />)}
                </div> : (
        /* Compact empty-state strip — mirrors the "No lessons today"
        row above so the section collapses to a tight, single-row
        layout instead of reserving a large hero of empty space. */
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isDark ? 'bg-navy-900/40 border-navy-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-8012">
                  <CalendarIcon className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8013" />
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8014">
                    No upcoming lessons
                  </span>
                </div>)}
            </section>}

          {/* Past Lessons Section */}
          {showPast && <section data-id="element-8015">
              <SectionHeader title="Past Lessons" count={pastLessons.length} data-id="element-8016" />
              {pastLessons.length > 0 ? <>
                  <div className="space-y-3" data-id="element-8017">
                    {/*
                      Cap the rendered list at PAST_LESSONS_LIMIT to keep
                      the main schedule page light. Older lessons are
                      reachable via the 'View All Past Lessons →' CTA
                      below (route TBD — see TODO).
                     */}
                    {pastLessons.slice(0, PAST_LESSONS_LIMIT).map(lesson => <LessonCard key={lesson.id} lesson={lesson} isPast data-id="element-8018" />)}
                  </div>
                  {pastLessons.length > PAST_LESSONS_LIMIT && <div className="mt-4 flex justify-end" data-id="element-8019">
                      <button type="button" onClick={() => {
              // TODO: navigate to the dedicated full
              // past-lessons history route once it's built.
              // For now this is intentionally a no-op so the
              // CTA is visible but non-destructive — the
              // history view is a follow-up scope.
            }} className={`text-sm font-medium transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-8020">
                        View All Past Lessons ({pastLessons.length}) →
                      </button>
                    </div>}
                </> : (
        /* Compact empty-state strip — mirrors the "No lessons today"
        row above so the section collapses to a tight, single-row
        layout instead of reserving a large hero of empty space. */
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isDark ? 'bg-navy-900/40 border-navy-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-8021">
                  <CalendarIcon className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8022" />
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8023">
                    No past lessons yet
                  </span>
                </div>)}
            </section>}
        </div>}

      {/* Lesson Detail Modal */}
      <StudentLessonDetailModal isOpen={selectedLesson !== null} lesson={selectedLesson} onClose={() => setSelectedLesson(null)} isDark={isDark} data-id="element-8024" />

      {/* Cancel Lesson Modal */}
      <StudentCancelLessonModal isOpen={lessonToCancel !== null} onClose={() => setLessonToCancel(null)} onConfirm={handleCancelLesson} lessonTitle={lessonToCancel?.title} teacherName={lessonToCancel?.teacherName} isDark={isDark} data-id="element-8025" />
    </div>;
};